import { Component, DestroyRef, effect, inject, signal, ViewChild } from '@angular/core';
import { TreeRootComponent } from '../tree-root/tree-root.component';
import { TreeItemService } from '../../../../shared/services/tree-item.service';
import { MaterialModule } from '../../../../modules/material.module';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { uniqBy } from 'lodash';
import { SEARCH_KEYWORDS } from '../../../../shared/utils/constant';
import {
  IActionMapping,
  ITreeOptions,
  KEYS,
  TREE_ACTIONS,
} from '@ali-hm/angular-tree-component';
import { TreeEventType } from '../../../../shared/utils/enums';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ItemPath } from '../../../../shared/utils/types';
import { AccountService } from '../../../../shared/services/account.service';
import { LimitReachedDialogComponent } from '../limit-reached-dialog/limit-reached-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DEFAULT_ITEM_VALUE } from '../utils/constant';
import { nanoid } from 'nanoid';
import { AuthService } from '../../../../authentication/auth.service';
import { ItemFormDialogComponent } from '../item-form-dialog/item-form-dialog.component';

// a constant mapping various user input actions to tree behavior
const actionMapping: IActionMapping = {
  mouse: {
    click: (tree, node, $event) => {
      //to make node active and select
      setTimeout(() => {
        TREE_ACTIONS.TOGGLE_ACTIVE(tree, node, $event);
      }, 0);
    },
  },
  keys: {
    // pressing the Enter key
    [KEYS.ENTER]: (tree, node, $event) => alert(`This is ${node.data.title}`),
  },
};

@Component({
  selector: 'app-active-items',
  imports: [TreeRootComponent, MaterialModule, BreadcrumbComponent],
  templateUrl: './active-items.component.html',
  styleUrl: './active-items.component.scss',
  standalone: true,
})
export class ActiveItemsComponent {
  protected treeItemService = inject(TreeItemService);
  private destroyRef = inject(DestroyRef);
  private accountService = inject(AccountService);
  private readonly dialog = inject(MatDialog);
   private auth = inject(AuthService);

  @ViewChild('treeRootItem', { static: false }) private treeRootItem: TreeRootComponent;

  displayedTreeNodeItems = signal<Array<any>>([]);
  itemPath = signal<ItemPath[]>([]); // an array of objects representing the path of the currently zoomed item
  private itemBeingEdited: any; // dictionary containing data of item currently displayed in "edit item" dialog box (if any)

  constructor() {
    effect(() => {
      if (this.treeItemService.allTreeNodeItems().length) {
        this.refreshItems();
      }
    });
  }

  ngOnInit(): void {

    this.itemBeingEdited = DEFAULT_ITEM_VALUE;

    const self = this;
    window.addEventListener('hashchange', function () {
      self.refreshItems();
    });

    this.treeItemService.treeEvent$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        const { type, data: info } = data;
        switch (type) {

          case TreeEventType.AddChildItem:
            self.addNewItem(info);
            break;

          case TreeEventType.EditItem:
            this.itemBeingEdited = info;
            this.showEditItemDialog();
            break;

          case TreeEventType.ExpandNode:
            this.expandNodeById(info);
            break;

          case TreeEventType.BlurFocusedNode:
            this.blurFocusedNodes();
            break;

          default:
            break;
        }
      });
  }

  /**
   * add an item to the bottom of the item tree
   */
  addNewItem(parentId: string = null): void {
    if (this.checkLimitReached()) {
      this.showLimitReachedDialog();
    } else {
      this.itemBeingEdited = this.generateNewItem(parentId, -1);
      this.showEditItemDialog();
    }
  }

   /**
   * this user has reached their limit for number of items they are allowed to add per month, If they are a Pro user there is no limit
   */
  private checkLimitReached(): boolean {
    return this.accountService.subscription().isProAccount
      ? false
      : this.accountService.subscription().numItemsAddedThisMonth >= this.accountService.subscription().maxNumItemsAdd;
  }

  /**
   *  a newly generated item with parent id and a display order value
   */
  private generateNewItem(parentId, displayOrder) {
    const id = nanoid(12);
    let sharedWith = [
      {
        type: 'Explicit',
        user: {
          display_name: this.auth.getCurrentUser().displayName,
          firebase_uid: this.auth.getCurrentUser().uid,
          email: this.auth.getCurrentUser().email,
        },
      },
    ];
    if (parentId) {
      const parentNode = this.treeItemService.getItemById(parentId);
      sharedWith = parentNode.shared_with.map((perm) => ({
        ...perm,
        type: 'Inherited',
      }));
    }

    const newItem = {
      id: id,
      new: true,
      display_order: displayOrder,
      subitems_expanded: false,
      hasChildren: false,
      in_trash: false,
      children: [],
      parent_id: parentId,
      title: '',
      type: '',
      status: '',
      priority: '',
      description: '',
      permission_type: 'Explicit',
      assignees: [],
      subscribers: [],
      shared_with: sharedWith,
      tags: [],
      history: [],
    };

    return newItem;
  }

  /**
   * display a modal dialog informing user that in order to add more items they should upgrade to a Pro account
   */
  private showLimitReachedDialog(): void {
    this.dialog.open(LimitReachedDialogComponent, {
      data: { maxNumAdds: this.accountService.subscription().maxNumItemsAdd },
    });
  }

  /**
     * Displays a dialog box that enables the user to edit an item
     */
    private showEditItemDialog(): void {
      this.dialog.open(ItemFormDialogComponent, {
        data: {
          itemBeingEdited: this.itemBeingEdited,
        },
        disableClose: true,
        panelClass: 'item-form-dialog'
      });
    }

  onMovedNode($event: any): void {
    this.treeItemService.setTreeEvent({
      type: TreeEventType.OnMovedNode,
      data: $event,
    });
  }

  private expandNodeById(id: string): void {
    this.treeRootItem.expandById(id);
  }

  private blurFocusedNodes(): void {
    this.treeRootItem.blurFocusedNodes();
  }

  /**
   * given text string, filter the item tree to just those nodes whose headers contain that text string (not case sensitive), as well as their parents up the hierarchy - additionally, if includes certain special keywords (e.g., "status:"), additionally filter out any items whose status field does not match the following value
   */
  applyTextFilter(text: string = '') {
    text = text?.toLowerCase().trim(); // clean text, make lower case
    const zoomedItem = this.getZoomedItem();
    this.zoomToItem(zoomedItem);

    if (!text && !zoomedItem) {
      return this.treeItemService.allTreeNodeItems().filter((i) => !i.in_trash);
    }

    // parse input text as list of query objects by default query item headers
    const parsedQuery = [
      {
        keyword: { name: 'header', value: '' },
        index: 0,
        value: '',
      },
    ];

    // for each instance of keyword in text, push an additional query field object into array
    for (const keyword of SEARCH_KEYWORDS) {
      let keywordIndex = text.indexOf(keyword.value);

      while (keywordIndex >= 0) {
        parsedQuery.push({
          keyword: keyword,
          index: keywordIndex,
          value: '',
        });
        keywordIndex = text.indexOf(keyword.value, keywordIndex + 1); // set to index of next instance of keyword
      }
    }

    // sort query field objects by index
    parsedQuery.sort((a, b) => a.index - b.index);

    // fill in values for each query field object
    for (let i = 0; i < parsedQuery.length; i++) {
      const queryField = parsedQuery[i];
      const nextQueryField = parsedQuery[i + 1];
      const startInd =
        queryField['index'] + queryField['keyword']['value'].length;
      const endInd = nextQueryField ? nextQueryField['index'] : text.length;
      queryField.value = text.substring(startInd, endInd).trim();
    }

    // get default tree that would be shown without any filter
    const defaultTree = zoomedItem
      ? [zoomedItem]
      : this.treeItemService.allTreeNodeItems();

    // get new tree to be displayed after filter applied
    const defaultArray = this.convertFromTree(defaultTree, []);
    const self = this;
    const newDisplayedArray = defaultArray.filter((item) =>
      self.isMatch(item, parsedQuery)
    );
    const uniqueDisplayedArray = uniqBy(newDisplayedArray, 'id');
    return uniqueDisplayedArray;
  }

  /**
   * if the browser url indicates that the tree should be zoomed in to a particular item
   */
  private refreshItems(): void {
    const filteredData = this.applyTextFilter(this.treeItemService.searchedText());
    this.displayedTreeNodeItems.set(this.treeItemService.convertToTreeStructure(filteredData));
  }

  /**
   * returns the tree node corresponding to the id specified in the URL anchor if it exists
   */
  private getZoomedItem() {
    const zoomedItemId = this.getUrlAnchorValue();
    if (!zoomedItemId) {
      return null;
    } else {
      return this.treeItemService.getItemById(zoomedItemId);
    }
  }

  /**
   * the segment of the current URL that follows the "/#/" substring, this segment of the URL indicates the item currently zoomed into
   */
  private getUrlAnchorValue(): string {
    const urlAnchor = window.location.hash;
    return urlAnchor == '' || urlAnchor == '#/' ? null : urlAnchor.substring(2); // remove '#/' prefix
  }

  /**
   * if item is not valid, then # change to default and set url anchor hash to indicate an empty string
   */
  private zoomToItem(item: any): void {
    if (!item) {
      window.location.hash = '#';
    }
    this.itemPath.set(this.getPath());
  }

  private getPath(): ItemPath[] {
    const zoomedItem = this.getZoomedItem();
    let path = [];
    if (zoomedItem) {
      path = this.getPathHelper(zoomedItem, path);
    }
    return path;
  }

  private getPathHelper(node, pathSoFar: Object[]): Object[] {
    pathSoFar.unshift({
      title: node.title,
      route: `#/${node.id}`,
    });
    if (node?.parent_id) {
      const parent = this.treeItemService.getItemById(node.parent_id);
      return this.getPathHelper(parent, pathSoFar);
    } else {
      // it is the parent of a root node
      return pathSoFar;
    }
  }

  private convertFromTree(itemTree: any[], arraySoFar: any[]): any[] {
    let outputArray = structuredClone(arraySoFar);

    for (const thisItem of itemTree) {
      outputArray.push(thisItem);

      // push its children to the array
      const itemChildren = this.treeItemService.getChildren(thisItem.id);
      if (itemChildren) {
        outputArray = this.convertFromTree(itemChildren, outputArray);
      }
    }

    return outputArray;
  }

  private isMatch(item, parsedQuery): boolean {
    let isMatch = true;

    // for each query field, set isMatch to false if item fields don't match logic
    for (const thisQueryField of parsedQuery) {
      // if queries by ancestor
      if (thisQueryField.keyword.name == 'isDescendentOf') {
        const ancestorId = thisQueryField.value;
        const ancestor = this.treeItemService.getItemById(ancestorId);
        if (ancestor) {
          window.location.hash = '/'.concat(ancestorId);
        }
      }

      // if queries item header
      else if (thisQueryField.keyword.name == 'header') {
        // get words in query
        const wordsInQuery = thisQueryField.value
          .split(' ')
          ?.map((word) => word.trim());

        // get words in item header
        const itemHeader = [
          item.title,
          item.status,
          item.priority,
          item.tags.join(' '),
        ]
          .join(' ')
          .toLowerCase()
          .trim();

        const wordsInItemHeader = itemHeader
          .split(' ')
          ?.map((word) => word.trim());

        // check for any match
        isMatch = wordsInQuery.every((wordInQuery) =>
          wordsInItemHeader.some((wordInItemHeader) =>
            wordInItemHeader.includes(wordInQuery)
          )
        );
      }

      // if queries item tags
      else if (thisQueryField.keyword.name == 'tags') {
        // get tags in query
        const tagsInQuery = thisQueryField.value
          .split(' ')
          ?.map((tag) => tag.trim())
          ?.filter((tag) => tag);

        // get item tags
        const itemTags = item.tags.map((tag) => tag.trim());

        // check for any match
        isMatch = tagsInQuery.every((tagInQuery) =>
          itemTags.some((itemTag) => itemTag.includes(tagInQuery))
        );
      }

      // else
      else {
        const itemVal = item[thisQueryField.keyword.name].toLowerCase().trim();
        if (!itemVal.includes(thisQueryField.value)) {
          isMatch = false;
        }
      }
    }
    return isMatch;
  }

  customTemplateOptions: ITreeOptions = {
    displayField: 'title',
    isExpandedField: 'subitems_expanded',
    idField: 'id', // node id field
    actionMapping,
    nodeHeight: 23, // allotted height of the entire tree
    useVirtualScroll: true,
    animateExpand: true,
    animateSpeed: 5,
    animateAcceleration: 1.05,
    allowDrag: (node) => {
      return true;
    },
    allowDrop: (node) => {
      return true;
    },
  };
}
